import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Crown, Loader2, Sparkles, Zap, CreditCard } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { api, loadRazorpay, loginWithGoogle } from "@/lib/api";

const FREE_FEATURES = [
  "2 active projects",
  "Word-level auto transcription",
  "All animation presets & karaoke",
  "Emoji pops & brand presets",
  "Transparent WebM + MP4 export",
];

const COUNTRIES = [
  { code: "IN", label: "India" },
  { code: "GB", label: "United Kingdom" },
  { code: "US", label: "United States" },
  { code: "OTHER", label: "Other country" },
];

const guessCountry = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (/Kolkata|Calcutta/.test(tz)) return "IN";
    if (/London/.test(tz)) return "GB";
    const loc = (navigator.language || "").toUpperCase();
    if (loc.endsWith("-IN")) return "IN";
    if (loc.endsWith("-GB")) return "GB";
    if (loc.endsWith("-US")) return "US";
    if (/New_York|Chicago|Denver|Los_Angeles/.test(tz)) return "US";
  } catch (e) { /* noop */ }
  return "IN";
};

export default function Pricing() {
  const { user, refresh } = useAuth() || {};
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [billing, setBilling] = useState(null);
  const [country, setCountry] = useState(guessCountry());
  const [countryTouched, setCountryTouched] = useState(false);
  const [localPricing, setLocalPricing] = useState(null);

  useEffect(() => {
    api.getPlans().then(setPlans).catch(() => setPlans([])).finally(() => setLoading(false));
    if (user) api.getBilling().then(setBilling).catch(() => {});
  }, [user]);

  // Resolve backend-authoritative local pricing (IP-detected or manual country override).
  useEffect(() => {
    const c = countryTouched ? (country === "OTHER" ? "" : country) : undefined;
    api.getLocalPricing(c).then((lp) => {
      setLocalPricing(lp);
      if (!countryTouched && lp?.detected_country) {
        const known = COUNTRIES.find((x) => x.code === lp.detected_country);
        setCountry(known ? lp.detected_country : (lp.region_code === "IN" ? "IN" : "OTHER"));
      }
    }).catch(() => {});
  }, [country, countryTouched]);

  const pro = plans.find((p) => p.id === "pro-monthly") || plans[0];
  const isPro = billing?.is_pro || user?.role === "admin";
  const daysLeft = billing?.days_remaining || 0;
  const configured = pro?.razorpay_configured;
  const stripeConfigured = pro?.stripe_configured;
  const isIndia = localPricing ? localPricing.gateway === "razorpay" : country === "IN";
  const priceLabel = localPricing?.display || `₹${pro?.price ?? 129}`;
  const validityDays = localPricing?.validity_days ?? pro?.validity_days ?? 28;

  const payWithStripe = async () => {
    if (!user) { navigate("/login"); return; }
    if (!stripeConfigured) { toast.error("Card payments aren't available yet."); return; }
    setBusy(true);
    try {
      const data = await api.stripeCheckout(window.location.origin, country === "OTHER" ? "" : country);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  const upgrade = async () => {
    if (!user) { loginWithGoogle(); return; }
    if (!configured) { toast.error("Payments aren't live yet. Please check back soon."); return; }
    setBusy(true);
    try {
      await loadRazorpay();
      const data = await api.createOrder(country);
      const rzp = new window.Razorpay({
        key: data.key_id,
        order_id: data.order_id,
        amount: data.amount,
        currency: data.currency,
        name: "Caption Motion",
        description: `${data.plan.name} — ${data.plan.validity_days} days Pro (one-time)`,
        theme: { color: "#00F0FF" },
        prefill: { name: data.user.name || "", email: data.user.email || "" },
        handler: async (resp) => {
          try {
            const res = await api.verifyOrder({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`Pro activated — ${res.validity_days} days added!`);
            await refresh?.();
            navigate("/profile");
          } catch {
            toast.error("We couldn't verify your payment. Contact support if you were charged.");
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setBusy(false); });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
    }
    setBusy(false);
  };

  const cancel = null;

  const PRIMARY = "flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#00F0FF] py-3.5 text-base font-black text-black transition-transform hover:scale-[1.02] disabled:opacity-60";
  const SECONDARY = "flex w-full items-center justify-center gap-2 rounded-full border border-white/15 py-2.5 text-sm font-bold text-white transition-colors hover:border-white/30 disabled:opacity-60";
  const razorpayBtn = (primary) => configured ? (
    <button key="rzp" onClick={upgrade} disabled={busy} data-testid="upgrade-btn" className={primary ? PRIMARY : SECONDARY}>
      {busy && primary ? <Loader2 size={18} className="animate-spin" /> : (primary ? <Crown size={18} /> : <Zap size={15} />)}
      {user ? `Pay ₹${pro?.price ?? 129} with UPI / Cards` : "Login to get Pro"}
    </button>
  ) : null;
  const stripeBtn = (primary) => stripeConfigured ? (
    <button key="stripe" onClick={payWithStripe} disabled={busy} data-testid="stripe-upgrade-btn" className={primary ? PRIMARY : SECONDARY}>
      {busy && primary ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={primary ? 18 : 15} />}
      {user ? `Pay ${priceLabel} with international card` : "Login to get Pro"}
    </button>
  ) : null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="pricing-page">
      <Navbar />
      <div className="mx-auto max-w-5xl px-5 py-16 sm:py-24">
        <div className="mb-14 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#00F0FF]">
            <Sparkles size={13} /> Simple pricing
          </div>
          <h1 className="font-display text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">Turn timestamps into motion</h1>
          <p className="mx-auto mt-4 max-w-xl text-base text-[#A1A1AA]">Start free. Upgrade to Pro for unlimited projects and multilingual captions.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#00F0FF]" /></div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {/* Free */}
            <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-8" data-testid="plan-free">
              <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-[#A1A1AA]"><Zap size={16} /> Free</div>
              <div className="mt-5 flex items-end gap-1">
                <span className="font-display text-5xl font-black">₹0</span>
                <span className="mb-1.5 text-sm text-[#A1A1AA]">/forever</span>
              </div>
              <ul className="mt-8 space-y-3">
                {FREE_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-white/85"><Check size={17} className="mt-0.5 shrink-0 text-[#A1A1AA]" /> {f}</li>
                ))}
              </ul>
              <button disabled className="mt-8 w-full cursor-default rounded-full border border-white/15 py-3 text-sm font-bold text-[#A1A1AA]">
                {user && !isPro ? "Your current plan" : "Get started free"}
              </button>
            </div>

            {/* Pro */}
            <div className="relative overflow-hidden rounded-3xl border border-[#00F0FF]/40 bg-gradient-to-b from-[#8B5CF6]/10 to-[#00F0FF]/5 p-8 shadow-[0_0_60px_-15px_rgba(0,240,255,0.4)]" data-testid="plan-pro">
              <div className="absolute right-5 top-5 rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#00F0FF] px-3 py-1 text-[10px] font-black uppercase tracking-widest text-black">Most popular</div>
              <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-[#00F0FF]"><Crown size={16} /> {pro?.name || "Caption Motion Pro"}</div>
              <div className="mt-5 flex items-end gap-1">
                <span className="font-display text-5xl font-black" data-testid="pro-price">{priceLabel}</span>
                <span className="mb-1.5 text-sm text-[#A1A1AA]">/ {validityDays} days</span>
              </div>
              <div className="mt-1 text-xs font-semibold text-[#00F0FF]">One-time payment · No auto-renewal{localPricing?.detected_country ? ` · ${localPricing.detected_country}` : ""}</div>
              <ul className="mt-6 space-y-3">
                {(pro?.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-white"><Check size={17} className="mt-0.5 shrink-0 text-[#00F0FF]" /> {f}</li>
                ))}
              </ul>
              {isPro ? (
                <div className="mt-8 space-y-2">
                  <div className="w-full rounded-full bg-white/10 py-3 text-center text-sm font-bold text-white" data-testid="pro-active-badge">✓ Pro active{daysLeft ? ` · ${daysLeft} days left` : ""}</div>
                  <button onClick={payWithStripe} disabled={busy} data-testid="extend-btn"
                    className="flex w-full items-center justify-center gap-2 rounded-full border border-[#00F0FF]/40 py-2.5 text-sm font-bold text-[#00F0FF] transition-colors hover:bg-[#00F0FF]/10 disabled:opacity-60">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />} Get another {pro?.validity_days ?? 28} days
                  </button>
                </div>
              ) : (
                <div className="mt-8 space-y-3" data-testid="pro-checkout">
                  <div>
                    <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-widest text-[#666]">Billing country</label>
                    <select data-testid="country-select" value={country} onChange={(e) => { setCountryTouched(true); setCountry(e.target.value); }}
                      className="w-full rounded-xl border border-white/15 bg-[#0d0d0d] px-3 py-2.5 text-sm font-semibold text-white outline-none focus:border-[#00F0FF]/50">
                      {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                    <p className="mt-1 text-[10px] text-[#666]" data-testid="routing-hint">
                      {isIndia ? `India → Razorpay (UPI, cards, RuPay) · ${priceLabel}` : `International → Stripe (cards) · ${priceLabel}`}
                    </p>
                  </div>
                  {isIndia ? (
                    <>{razorpayBtn(true)}{!configured && stripeBtn(true)}</>
                  ) : (
                    <>{stripeBtn(true)}{!stripeConfigured && razorpayBtn(true)}{stripeConfigured && configured && razorpayBtn(false)}</>
                  )}
                  {!configured && !stripeConfigured && (
                    <p className="text-center text-[11px] text-[#A1A1AA]">Payments launching soon — you'll be able to get Pro here.</p>
                  )}
                  <p className="text-center text-[11px] text-[#666]">One-time {priceLabel} · {validityDays} days Pro · secure checkout</p>
                </div>
              )}

              {(() => {
                const methods = isIndia
                  ? (configured ? ["UPI", "Visa", "Mastercard", "RuPay"] : (stripeConfigured ? ["Visa", "Mastercard", "Amex"] : []))
                  : (stripeConfigured ? ["Visa", "Mastercard", "Amex"] : (configured ? ["UPI", "Visa", "Mastercard", "RuPay"] : []));
                if (!methods.length) return null;
                const tint = { UPI: "#3d9b35", Visa: "#1a1f71", Mastercard: "#eb001b", RuPay: "#0d7a3f", Amex: "#2e77bc" };
                return (
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5" data-testid="payment-methods" aria-label="Accepted payment methods">
                    <span className="mr-1 text-[10px] uppercase tracking-widest text-[#666]">Accepts</span>
                    {methods.map((m) => (
                      <span key={m} data-testid={`pay-badge-${m.toLowerCase()}`}
                        className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-[11px] font-bold text-[#D4D4D8]">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: tint[m] }} />{m}
                      </span>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
